import React from 'react';
import OptimizedImage from './OptimizedImage';

interface SmartImageProps {
  src?: string;
  alt: string;
  width?: number;
  height?: number;
  className?: string;
  aspectRatio?: 'square' | 'landscape' | 'portrait' | 'auto';
  loading?: 'lazy' | 'eager';
  fallbackSrc?: string;
}

const SmartImage: React.FC<SmartImageProps> = ({
  src,
  alt,
  width = 800,
  height,
  className = '',
  aspectRatio = 'auto',
  loading = 'lazy',
  fallbackSrc = 'https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?w=800&h=400&fit=crop&crop=center&auto=format&q=80'
}) => {
  // Use fallback if src is missing or empty
  const imageSrc = src && src.trim() !== '' ? src : fallbackSrc;
  
  // Check if the image is from Cloudinary (but not if it's our fallback placeholder)
  // Also handle the case where we only have a Cloudinary public ID (no protocol/domain)
  const hasProtocol = /^https?:\/\//i.test(imageSrc) || imageSrc.startsWith('data:');
  const looksLikePublicId = imageSrc !== fallbackSrc && !hasProtocol; // e.g., "folder/asset-id" or just "asset-id"
  const isCloudinaryDomain = imageSrc.includes('cloudinary.com') || imageSrc.includes('res.cloudinary.com');
  const isCloudinaryImage = imageSrc !== fallbackSrc && (isCloudinaryDomain || looksLikePublicId);
  
  console.log('🖼️ SmartImage debug:', {
    originalSrc: src,
    imageSrc,
    isCloudinaryImage,
    alt: alt.substring(0, 30) + '...'
  });

  if (isCloudinaryImage) {
    // Use optimized Cloudinary component
    return (
      <OptimizedImage
        publicId={imageSrc}
        alt={alt}
        width={width}
        height={height}
        className={className}
        aspectRatio={aspectRatio}
        loading={loading}
      />
    );
  }

  // Fallback to regular img tag for non-Cloudinary images
  return (
    <img
      src={imageSrc}
      alt={alt}
      width={width}
      height={height}
      className={className}
      loading={loading}
      style={{ maxWidth: '100%', height: 'auto' }}
      onError={(e) => {
        const target = e.target as HTMLImageElement;
        if (target.src !== fallbackSrc) {
          target.src = fallbackSrc;
        }
      }}
    />
  );
};

export default SmartImage;
